import { Avatar } from '../../ui/Avatar.js';
import { cx } from '../../ui/cx.js';

/** Mots qui ne portent pas l'identité du garage (« Garage de la Gare » → « GG »). */
const MINOR = new Set(['de', 'du', 'des', 'la', 'le', 'les', 'l', 'd', 'et', 'à', 'au', 'aux', '&']);

/**
 * Monogramme du garage : initiales des deux premiers mots significatifs (« Garage Démo A » → « GD »).
 * Calculé depuis le nom renvoyé par l'API, jamais écrit en dur.
 */
export function garageMonogram(name: string): string {
  const words = name
    .split(/[\s'’-]+/)
    .map((w) => w.trim())
    .filter((w) => w && !MINOR.has(w.toLowerCase()));
  return words
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

/**
 * Marque du garage : son icône ou son logo (tuile blanche), sinon son monogramme lisible aux couleurs
 * de la marque (`bg-mark text-on-mark`, contraste calculé par theme.ts). Utilisé par l'en-tête, la barre latérale et le portail.
 */
export function BrandMark({
  name,
  logoUrl,
  iconUrl,
  size = 'md',
}: {
  name: string;
  logoUrl: string | null | undefined;
  /** Vague 5 (A3) : icône carrée du garage, prioritaire sur le logo (barre latérale, en-tête mobile). */
  iconUrl?: string | null | undefined;
  size?: 'sm' | 'md' | 'lg';
}) {
  const box = { sm: 'size-8', md: 'size-10', lg: 'size-11' }[size];
  if (iconUrl) {
    return (
      <span className={cx('grid shrink-0 place-items-center overflow-hidden rounded-lg bg-logo ring-1 ring-black/10', box)}>
        <img src={iconUrl} alt="" className="size-full object-cover" />
      </span>
    );
  }
  if (logoUrl) {
    return (
      <span className={cx('grid shrink-0 place-items-center overflow-hidden rounded-lg bg-logo ring-1 ring-black/10', box)}>
        <img src={logoUrl} alt="" className="size-full object-contain" />
      </span>
    );
  }
  const mono = garageMonogram(name);
  if (!mono) return null;
  // `Avatar` calcule ses initiales à partir du nom : on lui passe le monogramme espacé.
  return (
    <Avatar
      name={mono.split('').join(' ')}
      tone="brand"
      size={size === 'sm' ? 'sm' : size === 'md' ? 'md' : 'lg'}
      className={cx('rounded-lg', size === 'lg' && 'size-11')}
    />
  );
}
